import { useState } from 'react';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

/**
 * Custom hook for assigning conversations to agents
 *
 * Features:
 * - Wraps assign_user_to_conversation RPC call
 * - Handles loading states
 * - Error handling with i18n toast notifications
 * - Tenant-aware (uses get_user_tenant_schema RPC)
 *
 * CRITICAL BUSINESS RULE:
 * - Cannot unassign conversations (user_id must not be NULL)
 * - Once assigned, conversation stays assigned forever
 */
export function useAssignConversation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('conversations');
  const [isAssigning, setIsAssigning] = useState(false);

  /**
   * Assign a conversation to a specific agent
   *
   * @param conversationId - UUID of the conversation to assign
   * @param targetUserId - UUID of the agent to assign (auth.users.id, NOT public.users.id)
   * @param targetUserName - Full name of the agent (for toast notification)
   * @returns Promise<boolean> - true if assigned successfully, false otherwise
   */
  const assignConversation = async (
    conversationId: string,
    targetUserId: string,
    targetUserName: string
  ): Promise<boolean> => {
    if (!user) {
      console.error('[useAssignConversation] No authenticated user');
      toast({
        title: t('assignment.error', 'Assignment Error'),
        description: t('assignment.notAuthenticated', 'You must be logged in to assign conversations'),
        variant: 'destructive',
      });
      return false;
    }

    setIsAssigning(true);

    try {
      // Get tenant schema dynamically
      const { data: schema, error: schemaError } = await supabase.rpc('get_user_tenant_schema');

      if (schemaError || !schema) {
        console.error('[useAssignConversation] Error getting tenant schema:', schemaError);
        toast({
          title: t('assignment.error', 'Assignment Error'),
          description: t('assignment.schemaError', 'Could not determine tenant schema'),
          variant: 'destructive',
        });
        return false;
      }

      console.log(`[useAssignConversation] Assigning conversation ${conversationId} to user ${targetUserId} in schema ${schema}`);

      // Call RPC to assign conversation
      // NOTE: user_id parameter expects auth.users.id (from frontend), NOT public.users.id
      // The RPC function handles the lookup from auth_user_id to public.users.id
      const { data: success, error: assignError } = await supabase.rpc('assign_user_to_conversation', {
        tenant_schema: schema,
        conversation_id: conversationId,
        user_id: targetUserId, // auth.users.id
      });

      if (assignError) {
        console.error('[useAssignConversation] RPC error:', assignError);
        toast({
          title: t('assignment.error', 'Assignment Error'),
          description: assignError.message || t('assignment.genericError', 'Failed to assign conversation'),
          variant: 'destructive',
        });
        return false;
      }

      if (!success) {
        console.warn('[useAssignConversation] RPC returned FALSE (user or conversation not found)');
        toast({
          title: t('assignment.error', 'Assignment Error'),
          description: t('assignment.notFound', 'User or conversation not found'),
          variant: 'destructive',
        });
        return false;
      }

      // Success!
      console.log(`[useAssignConversation] ✅ Successfully assigned to ${targetUserName}`);
      toast({
        title: t('assignment.success', 'Assigned Successfully'),
        description: t('assignment.assignedTo', 'Assigned to {{agentName}}', { agentName: targetUserName }),
      });

      return true;

    } catch (error) {
      console.error('[useAssignConversation] Unexpected error:', error);
      toast({
        title: t('assignment.error', 'Assignment Error'),
        description: t('assignment.unexpectedError', 'An unexpected error occurred'),
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsAssigning(false);
    }
  };

  return {
    assignConversation,
    isAssigning,
  };
}
